import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

/**
 * Modal para SOLICITAR operadores de apoyo a un servicio pasillera (desde el front).
 * El coordinador elige los operadores + observaciones y los manda; quedan en
 * estado SOLICITADO. Luego, en el handheld, el principal confirma su presencia por NFC.
 *
 * Props:
 *  - servicio: objeto del servicio pasillera (o null para cerrado). Debe traer _id.
 *  - onClose: () => void
 */
const ModalSolicitarApoyo = ({ servicio, onClose }) => {
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : null;
  const estacion = decoded?.estacion?.trim().toUpperCase();

  const [colaboradores, setColaboradores] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!servicio) return;
    setSeleccionados([]);
    setObservaciones("");
    setBusqueda("");
    const fetchOperadores = async () => {
      try {
        setCargando(true);
        const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const disponibles = (res.data || []).filter(
          (c) => c.estacion?.trim().toUpperCase() === estacion
        );
        setColaboradores(disponibles);
      } catch (error) {
        toast.error("Error al obtener operadores");
      } finally {
        setCargando(false);
      }
    };
    fetchOperadores();
  }, [servicio, token, estacion]);

  if (!servicio) return null;

  const servicioId = servicio._id || servicio.noServicio;

  const toggle = (c) => {
    setSeleccionados((prev) =>
      prev.some((op) => op.noColaborador === c.noColaborador)
        ? prev.filter((op) => op.noColaborador !== c.noColaborador)
        : [...prev, c]
    );
  };

  const solicitar = async () => {
    if (isSubmitting) return;
    if (seleccionados.length === 0) {
      toast.warning("⚠️ Selecciona al menos un operador de apoyo");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}api/servicios/${servicioId}/apoyos`,
        {
          operadores: seleccionados.map((c) => ({
            noColaborador: c.noColaborador,
            siglas: c.siglas,
          })),
          observaciones,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("✅ Apoyo(s) solicitado(s). El principal confirma por NFC al llegar.");
      onClose();
    } catch (error) {
      console.error("❌ Error al solicitar apoyos:", error);
      toast.error(error.response?.data?.error || "Error al solicitar apoyos");
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibles = colaboradores.filter((c) =>
    (c.siglas || "").toUpperCase().includes(busqueda)
  );

  return (
    <div className="modal-overlay-pasajeros" onClick={onClose}>
      <div className="modal-pasajeros" onClick={(e) => e.stopPropagation()}>
        <h3>
          Solicitar apoyos · {servicio.nombrePax || ""} ({servicio.noVuelo} {servicio.aerolinea})
        </h3>
        <p style={{ margin: "4px 0 12px", color: "#64748b", fontSize: "0.9rem" }}>
          Pasillera: elegí los operadores de apoyo. Les llega al handheld y el principal
          confirma su presencia escaneando la credencial NFC cuando llegan.
        </p>

        <input
          type="text"
          placeholder="Buscar por siglas..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value.toUpperCase())}
          className="input-busqueda"
          style={{ marginBottom: "10px", width: "100%", padding: "6px" }}
        />

        <div className="tabla-scroll" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {cargando ? (
            <p style={{ padding: 12 }}>Cargando operadores...</p>
          ) : (
            <table className="tabla-pasajeros">
              <thead>
                <tr>
                  <th></th>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Siglas</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c, idx) => (
                  <tr key={c.noColaborador}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionados.some((op) => op.noColaborador === c.noColaborador)}
                        onChange={() => toggle(c)}
                      />
                    </td>
                    <td>{idx + 1}</td>
                    <td>{c.nombreCompleto}</td>
                    <td>{c.siglas}</td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 12 }}>
                      Sin operadores en turno.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ margin: "12px 0" }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Observaciones
          </label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Ej. pasajero de alto peso, se requieren 3 personas"
            rows={2}
            style={{ width: "100%", padding: 8, resize: "vertical" }}
          />
        </div>

        <div className="text-end">
          <button className="btn-registrar" onClick={solicitar} disabled={isSubmitting}>
            {isSubmitting ? "Solicitando..." : "Solicitar apoyos"}
          </button>
          <button className="btn-cerrar" onClick={onClose} disabled={isSubmitting}>
            ✖ Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalSolicitarApoyo;
